import React, { useState } from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Loader from '@amzn/meridian/loader';
import Icon from '@amzn/meridian/icon';
import Expander from '@amzn/meridian/expander';
import alertIcon from '@amzn/meridian-tokens/base/icon/alert';
import checkCircleIcon from '@amzn/meridian-tokens/base/icon/check-circle';

import { isRiskScanning, hasActiveRisks, getActiveRiskCount, RISK_STATUS } from './riskEngineModel';

/**
 * One group per guardrail. Intervals with issues are listed inside the expander.
 */
const RiskGroup = ({ displayName, items }) => {
  const [open, setOpen] = useState(false);
  return (
    <Expander type="list" title={<Text type="b300">{displayName}</Text>} open={open} onChange={setOpen}>
      <ul style={{ margin: 0, paddingLeft: 16 }}>
        {items.map((result, idx) => (
          <li key={idx} style={{ marginBottom: 4 }}>
            <Text type="b100">{result.explanation}</Text>
          </li>
        ))}
      </ul>
    </Expander>
  );
};

/** Active Risks panel rendered above the Approve/Reject row when a plan is pending approval. */
const ActiveRisksContainer = ({ riskEvaluation }) => {
  if (isRiskScanning(riskEvaluation)) {
    return (
      <Box spacingInset="400" width="100%" className="risk-alert-scanning">
        <Row spacing="300" alignmentVertical="center">
          <Loader size="small" />
          <Text type="b300">Scanning for active risks…</Text>
        </Row>
      </Box>
    );
  }

  if (!hasActiveRisks(riskEvaluation)) {
    if (riskEvaluation.status === RISK_STATUS.FAILED) {
      return (
        <Box spacingInset="400" width="100%" className="risk-alert-error">
          <Row spacing="300" alignmentVertical="center">
            <Icon tokens={alertIcon} />
            <Text type="b300">Risk analysis unavailable.</Text>
          </Row>
        </Box>
      );
    }
    return (
      <Box spacingInset="400" width="100%" className="risk-alert-success">
        <Row spacing="300" alignmentVertical="center">
          <Icon tokens={checkCircleIcon} />
          <Text type="b300">No active risks detected.</Text>
        </Row>
      </Box>
    );
  }

  const count = getActiveRiskCount(riskEvaluation);
  return (
    <Box spacingInset="400" width="100%" className="risk-alert-error">
      <Column spacing="400" width="100%">
        <Row spacing="300" alignmentVertical="center">
          <Icon tokens={alertIcon} />
          <Text type="h300">Active Risks ({count})</Text>
        </Row>
        <Column spacing="300">
          {Object.entries(
            (riskEvaluation.results || [])
              .filter((r) => r.breached)
              .reduce((groups, result) => {
                const key = result.displayName || result.guardrailId;
                (groups[key] = groups[key] || []).push(result);
                return groups;
              }, {})
          ).map(([displayName, items]) => (
            <RiskGroup key={displayName} displayName={displayName} items={items} />
          ))}
        </Column>
      </Column>
    </Box>
  );
};

export default ActiveRisksContainer;
